"use client";

type DesktopInvoke = <T = unknown>(
  command: string,
  args?: Record<string, unknown>,
) => Promise<T>;

type DesktopWindow = Window & {
  __TAURI_INTERNALS__?: {
    invoke?: DesktopInvoke;
  };
  __FINDIN_DESKTOP_INFO__?: {
    desktopDiagnosticsVersion?: number;
  };
};

type NativeCopyResult = {
  success?: unknown;
  code?: unknown;
};

type NativeReportResult = NativeCopyResult & {
  report?: unknown;
};

export type DesktopDiagnosticCopyResult =
  | { success: true; code: "COPIED" }
  | {
    success: false;
      code: "UNAVAILABLE" | "CLIPBOARD_WRITE_FAILED" | "COMMAND_FAILED";
  };

export type DesktopDiagnosticReportResult =
  | { success: true; code: "READY"; report: string }
  | {
    success: false;
    code: "UNAVAILABLE" | "INVALID_RESPONSE" | "COMMAND_FAILED";
  };

function getDesktopWindow(): DesktopWindow | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window as DesktopWindow;
}

function getDesktopInvoke(): DesktopInvoke | null {
  const invoke = getDesktopWindow()?.__TAURI_INTERNALS__?.invoke;
  return typeof invoke === "function" ? invoke : null;
}

export function isDesktopDiagnosticsRuntime() {
  const desktopWindow = getDesktopWindow();
  const capabilityVersion = desktopWindow?.__FINDIN_DESKTOP_INFO__?.desktopDiagnosticsVersion;
  return getDesktopInvoke() !== null
    && typeof capabilityVersion === "number"
    && capabilityVersion >= 1;
}

export function isDesktopIssueReportingRuntime() {
  const desktopWindow = getDesktopWindow();
  const capabilityVersion = desktopWindow?.__FINDIN_DESKTOP_INFO__?.desktopDiagnosticsVersion;
  return getDesktopInvoke() !== null
    && typeof capabilityVersion === "number"
    && capabilityVersion >= 2;
}

export async function copyDesktopDiagnostics(): Promise<DesktopDiagnosticCopyResult> {
  if (!isDesktopDiagnosticsRuntime()) {
    return { success: false, code: "UNAVAILABLE" };
  }
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return { success: false, code: "UNAVAILABLE" };
  }

  try {
    const result = await invoke<NativeCopyResult>("copy_desktop_diagnostics");
    if (result?.success === true && result.code === "COPIED") {
      return { success: true, code: "COPIED" };
    }
    if (result?.success === false && result.code === "CLIPBOARD_WRITE_FAILED") {
      return { success: false, code: "CLIPBOARD_WRITE_FAILED" };
    }
    return { success: false, code: "COMMAND_FAILED" };
  } catch (error) {
    console.warn("[Findin Desktop] failed to copy diagnostics:", error);
    return { success: false, code: "COMMAND_FAILED" };
  }
}

export async function getDesktopDiagnosticsReport(): Promise<DesktopDiagnosticReportResult> {
  if (!isDesktopIssueReportingRuntime()) {
    return { success: false, code: "UNAVAILABLE" };
  }
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return { success: false, code: "UNAVAILABLE" };
  }

  try {
    const result = await invoke<NativeReportResult>("get_desktop_diagnostics");
    if (
      result?.success === true
      && result.code === "READY"
      && typeof result.report === "string"
      && result.report.length > 0
    ) {
      return { success: true, code: "READY", report: result.report };
    }
    return { success: false, code: "INVALID_RESPONSE" };
  } catch {
    console.warn("[Findin Desktop] failed to read diagnostics for issue reporting");
    return { success: false, code: "COMMAND_FAILED" };
  }
}
